import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';

import { useTemplateStore } from '@/stores/template-store';
import { duplicateTemplate as duplicateTemplateService } from '@/services/template-service';
import type { TemplateTask } from '@/types/checklist-template';
import type { Task } from '@/types/task';
import { Button } from '@/components/common/Button';
import {
  formatTimingDescription,
  groupTasksByCategory,
} from '@/utils/template-display-utils';
import { TaskRow } from '@/components/tasks/TaskRow';
import { TemplateSelectionFlow } from '@/components/templates/TemplateSelectionFlow';

/**
 * Adapter: maps a TemplateTask to the minimal Task shape TaskRow requires in preview mode.
 * Only used for preview rendering — no ID conflicts or persistence concerns.
 */
function adaptTemplateTaskForPreview(task: TemplateTask): Task {
  return {
    id: task.id,
    templateTaskId: task.id,
    seasonId: '__preview__',
    title: task.title,
    description: task.description,
    category: task.category,
    selectedAction: undefined,
    dueDate: new Date().toISOString().slice(0, 10),
    timingAnchor: task.timingAnchor,
    offsetDays: task.offsetDays,
    dependsOn: task.dependsOn,
    blocked: false,
    completed: false,
    sortOrder: task.sortOrder,
    notes: task.notes,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
}

function weaponLabel(w: string): string {
  const map: Record<string, string> = {
    gun: 'Gun', bow: 'Bow', muzzleloader: 'Muzzleloader', crossbow: 'Crossbow',
    youth: 'Youth', any: 'Any', other: 'Other',
  };
  return map[w] ?? w;
}

interface PreviewCategorySectionProps {
  label: string;
  tasks: TemplateTask[];
}

function PreviewCategorySection({ label, tasks }: PreviewCategorySectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="bg-[var(--color-surface)] border-[0.5px] border-[var(--color-border)] rounded-lg shadow-card overflow-hidden">
      <button
        onClick={() => setIsOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-[var(--color-elevated)] transition-colors"
        aria-expanded={isOpen}
        data-expanded={isOpen ? 'true' : 'false'}
      >
        <span className="field-label">{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-body-xs text-[var(--color-text-muted)]">
            {tasks.length}
          </span>
        </div>
      </button>

      {isOpen && (
        <div>
          {tasks.map((task) => (
            <TaskRow
              key={task.id}
              task={adaptTemplateTaskForPreview(task)}
              seasonId="__preview__"
              onToggleComplete={() => { /* no-op in preview */ }}
              preview={true}
              previewSubtext={formatTimingDescription(task)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function TemplateDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { templates, isLoading, loadTemplates, getTemplate, removeTemplate } = useTemplateStore();
  const [showSelectionFlow, setShowSelectionFlow] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (templates.length === 0) void loadTemplates();
  }, [templates.length, loadTemplates]);

  const template = id ? getTemplate(id) : undefined;

  async function handleDuplicate() {
    if (!template) return;
    setIsDuplicating(true);
    try {
      const copy = await duplicateTemplateService(template.id);
      await loadTemplates();
      navigate(`/prep/templates/${copy.id}/edit`);
    } catch (err) {
      console.error('TemplateDetailPage.handleDuplicate:', err);
    } finally {
      setIsDuplicating(false);
    }
  }

  async function handleDelete() {
    if (!template) return;
    setIsDeleting(true);
    try {
      await removeTemplate(template.id);
      navigate('/templates');
    } catch (err) {
      console.error('TemplateDetailPage.handleDelete:', err);
      setIsDeleting(false);
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-[var(--color-text-muted)] text-body-sm">Loading template...</p>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="flex flex-col gap-4 py-8">
        <p className="text-[var(--color-text-muted)] text-body-sm">Template not found.</p>
        <Link to="/prep/templates">
          <Button variant="ghost">Back to Templates</Button>
        </Link>
      </div>
    );
  }

  const groups = groupTasksByCategory(template.tasks);
  const categoryCount = groups.length;

  return (
    <div className="flex flex-col gap-6 pb-24">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/templates"
          className="text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)] transition-colors"
          aria-label="Back to templates"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </Link>
        <h1
          className="font-display text-display-md text-[var(--color-text-display)] flex-1 line-clamp-2"
          data-typography="display-md"
        >
          {template.name}
        </h1>
      </div>

      {/* Template metadata card */}
      <div className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-lg shadow-card p-4 flex flex-col gap-3">
        {template.description && (
          <p className="text-body-sm text-[var(--color-text-secondary)]">{template.description}</p>
        )}
        <div className="flex flex-wrap gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent-hover)] ring-1 ring-[var(--color-accent-pressed)]/40">
            {weaponLabel(template.weaponType)}
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-elevated)] text-[var(--color-text-muted)] ring-1 ring-[var(--color-border)] capitalize">
            {template.region}
          </span>
          {template.isBuiltIn && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-body-xs font-medium bg-[var(--color-accent-subtle)] text-[var(--color-accent)] ring-1 ring-[var(--color-accent)]/30">
              Built-in
            </span>
          )}
        </div>
        <p className="text-body-xs text-[var(--color-text-muted)]">
          {template.tasks.length} tasks across {categoryCount}{' '}
          {categoryCount === 1 ? 'category' : 'categories'}
        </p>

        {/* Template actions */}
        <div className="flex gap-2 flex-wrap pt-1 border-t border-[var(--color-border)]">
          <Button
            variant="ghost"
            onClick={() => void handleDuplicate()}
            disabled={isDuplicating}
            data-testid="duplicate-btn"
          >
            {isDuplicating ? 'Duplicating...' : 'Duplicate'}
          </Button>
          {!template.isBuiltIn && (
            <Button
              variant="ghost"
              onClick={() => navigate(`/prep/templates/${template.id}/edit`)}
              data-testid="edit-btn"
            >
              Edit
            </Button>
          )}
          {!template.isBuiltIn && (
            <Button
              variant="danger"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="delete-btn"
            >
              Delete
            </Button>
          )}
        </div>

        {/* Delete confirmation */}
        {showDeleteConfirm && (
          <div
            data-testid="delete-confirm"
            className="bg-[var(--color-surface)] border border-red-800/50 rounded-lg p-4 flex flex-col gap-3"
          >
            <p className="text-body-sm text-[var(--color-text-primary)]">
              Are you sure you want to delete &quot;{template.name}&quot;?
            </p>
            {template.tasks.length > 0 && (
              <p className="text-body-xs text-[var(--color-text-muted)]">
                This template has been used to generate tasks. Deleting it won&apos;t affect
                existing tasks, but you won&apos;t be able to generate new tasks from it.
              </p>
            )}
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                disabled={isDeleting}
                onClick={() => void handleDelete()}
                data-testid="confirm-delete-btn"
              >
                {isDeleting ? 'Deleting...' : 'Delete Template'}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Task list grouped by category */}
      <div className="flex flex-col gap-3">
        {groups.map((group) => (
          <PreviewCategorySection
            key={group.category}
            label={group.label}
            tasks={group.tasks}
          />
        ))}
      </div>

      {/* Sticky "Use This Template" button */}
      <div className="fixed bottom-14 sm:bottom-0 left-0 right-0 p-4 bg-[var(--color-bg)]/90 backdrop-blur-sm border-t border-[var(--color-border)] z-10">
        <div className="max-w-2xl mx-auto">
          <Button
            variant="primary"
            className="w-full"
            onClick={() => setShowSelectionFlow(true)}
          >
            Use This Template
          </Button>
        </div>
      </div>

      {/* Template selection bottom sheet */}
      {showSelectionFlow && template && (
        <TemplateSelectionFlow
          templateId={template.id}
          templateName={template.name}
          weaponType={template.weaponType}
          onClose={() => setShowSelectionFlow(false)}
        />
      )}
    </div>
  );
}
